import { useContext, useEffect, useState } from "react";
import Select from "react-select";
import { getCmsLocationsRawDetail, getLocationTemplateProducts } from "api/cmsLocation";
import { OrderConfigContext } from "Context/OrderConfigContext";

export const defaultLocationTemplate = { template: "standard", product_source: "city", product_ids: [] };
export const locationTemplateKey = "location_template";

export function readLocationTemplate(contents = []) {
  try {
    const config = { ...defaultLocationTemplate, ...JSON.parse(contents.find(item => item.key === locationTemplateKey)?.content || "{}") };
    // "store" mode was removed: city mode falls back to the store's best sellers.
    return config.product_source === "store" ? { ...config, product_source: "city" } : config;
  } catch {
    return { ...defaultLocationTemplate };
  }
}

// The API also checks that the page or a parent page has a store.
export function locationTemplateError(config) {
  if (config.template !== "boutique") return "";
  if (config.product_source === "manual" && (config.product_ids.length !== 8 ||
    config.product_ids.some(id => !Number.isSafeInteger(id) || id <= 0) || new Set(config.product_ids).size !== 8)) {
    return "Choose eight different products for the boutique template.";
  }
  return "";
}

export const zipList = (zipcodes) => (zipcodes || "").split(",").map(zip => zip.trim()).filter(zip => /^\d{5}$/.test(zip));

// Same walk as the API: a page with no store uses the nearest parent page's store.
async function parentStoreId(parentId) {
  for (let id = parentId, depth = 0; id && depth < 5; depth++) {
    const location = (await getCmsLocationsRawDetail(id))?.data?.data;
    if (Number(location?.store_id) > 0) return Number(location.store_id);
    id = location?.parent_id;
  }
  return null;
}

export default function LocationTemplate({ value, onChange, storeId: ownStoreId, parentId, zipcodes }) {
  const { storeList } = useContext(OrderConfigContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inheritedStoreId, setInheritedStoreId] = useState(null);
  const boutique = value.template === "boutique";
  const manual = boutique && value.product_source === "manual";
  const storeId = ownStoreId || inheritedStoreId;
  const soldOut = products.filter(product => !product.available && value.product_ids.includes(product.value));

  useEffect(() => {
    let cancelled = false;
    setInheritedStoreId(null);
    if (!boutique || ownStoreId || !parentId) return;
    parentStoreId(parentId).then(id => { if (!cancelled) setInheritedStoreId(id); }).catch(() => {});
    return () => { cancelled = true; };
  }, [boutique, ownStoreId, parentId]);

  useEffect(() => {
    let cancelled = false;
    setProducts([]);
    setError("");
    if (!manual || !storeId) return;
    setLoading(true);
    getLocationTemplateProducts(storeId).then(response => {
      if (cancelled) return;
      if (response?.status !== 200) throw new Error("Products could not be loaded. Try selecting the store again.");
      setProducts((response.data.data || []).map(product => ({
        value: Number(product.id), available: Number(product.available) === 1,
        label: `${product.title}${product.sku_id ? ` — ${product.sku_id}` : ""}${Number(product.available) === 1 ? "" : " (sold out)"}`,
      })));
    }).catch(() => {
      if (!cancelled) setError("Products could not be loaded. Try selecting the store again.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [manual, storeId]);

  const updateProduct = (index, id) => {
    const ids = Array.from({ length: 8 }, (_, i) => value.product_ids[i] || null);
    ids[index] = id;
    onChange({ ...value, product_ids: ids });
  };
  const moveProduct = (index, direction) => {
    const ids = Array.from({ length: 8 }, (_, i) => value.product_ids[i] || null);
    [ids[index], ids[index + direction]] = [ids[index + direction], ids[index]];
    onChange({ ...value, product_ids: ids });
  };

  return (
    <section className="col-12 my-4" aria-label="Location page template">
      <label htmlFor="location-template"><strong>Page template</strong></label>
      <select id="location-template" className="form-control" value={value.template}
        onChange={event => onChange({ ...value, template: event.target.value })}>
        <option value="standard">Current location template</option>
        <option value="boutique">Boutique landing page — 8 products</option>
      </select>
      <p className="mt-2">Choose the layout for this page. Other location pages keep their own template.</p>
      {value.template === "boutique" && <>
        <p>To keep both versions of the same location live, create a separate page with a different URL slug.</p>
        <p>Use the first banner as the header image and Description for the text below it.</p>
        {!ownStoreId && <p>{inheritedStoreId
          ? `Store: ${storeList?.find(store => store.id === inheritedStoreId)?.title || inheritedStoreId}, from the parent page. Select a store below to override it.`
          : "Select a store below, or set one on the parent page."}</p>}
        <label htmlFor="location-product-source"><strong>Choose products</strong></label>
        <select id="location-product-source" className="form-control" value={value.product_source}
          onChange={event => onChange({ ...value, product_source: event.target.value })}>
          <option value="city">Automatic best sellers in this city</option>
          <option value="manual">Choose and arrange 8 products</option>
        </select>
        <p className="mt-2">Automatic selections use sales from the last 90 days. City selections use this page’s ZIP codes, then fill from the store’s best sellers. A city with fewer than 30 orders in that time uses the store’s best sellers. Unavailable products are replaced automatically.</p>
        {value.product_source === "city" && zipList(zipcodes).length === 0 && <p role="alert" className="alert alert-warning">
          This page has no ZIP codes, so it shows the store’s best sellers. Add the city’s ZIP codes in Zipcodes above to use this city’s sales.
        </p>}
        {manual && <>
          <p>Search by product name or SKU. All eight positions are required. Use the arrows to change the order.</p>
          {error && <p role="alert" className="text-danger">{error}</p>}
          {soldOut.length > 0 && <p role="alert" className="alert alert-warning">
            Sold out now: {soldOut.map(product => product.label.replace(" (sold out)", "")).join(", ")}. You can still save. The site skips these until they are back and fills the places with best sellers.
          </p>}
          {Array.from({ length: 8 }, (_, index) => {
            const id = value.product_ids[index];
            const selected = products.find(product => product.value === id);
            return <div className="d-flex align-items-center mb-3" key={index}>
              <label htmlFor={`location-product-${index}`} className="mr-3 mb-0">{index + 1}.</label>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select inputId={`location-product-${index}`} aria-label={`Product ${index + 1}`}
                  placeholder={storeId ? "Search products…" : "Choose a store first"}
                  isDisabled={!storeId || loading} isLoading={loading}
                  value={selected || (id ? { value: id, label: loading ? "Loading product…" : `Product ${id} — unavailable; choose a replacement` } : null)}
                  options={products.filter(product => product.value === id || !value.product_ids.includes(product.value))}
                  onChange={product => updateProduct(index, product?.value || null)} />
              </div>
              <button type="button" className="btn btn-outline-secondary ml-2" aria-label={`Move product ${index + 1} up`}
                disabled={index === 0} onClick={() => moveProduct(index, -1)}>↑</button>
              <button type="button" className="btn btn-outline-secondary ml-1" aria-label={`Move product ${index + 1} down`}
                disabled={index === 7} onClick={() => moveProduct(index, 1)}>↓</button>
            </div>;
          })}
        </>}
      </>}
    </section>
  );
}
